import React from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { ActionButton } from '@contrast-security-inc/design-system-actions'
import {
  PopoverMenu,
  MenuTrigger,
  MenuItem,
  Menu,
  Tooltip
} from '@contrast-security-inc/design-system-popovers'
import {
  IconSend,
  IconSize
} from '@contrast-security-inc/design-system-foundations'

const maxSendCount = 50

export const actionTestID = 'vuln-send-test-id'

interface Props {
  readonly vulnerabilityCount: number
  readonly onSelectEmailOption: () => void
  readonly onSelectBugtrackerOption: () => void
  readonly vulnerabilitiesAreFromSameApplication: boolean
  setIsRelatedPopoverOpen?: (isOpen: boolean) => void
  size?: IconSize
}

export const sendActionMenuBugTrackerTestId = 'send-bug-tracker-menu-option'
export const sendActionMenuEmailTestId = 'send-email-menu-option'

export function SendActionMenu({
  vulnerabilityCount,
  vulnerabilitiesAreFromSameApplication,
  onSelectEmailOption,
  onSelectBugtrackerOption,
  setIsRelatedPopoverOpen,
  size
}: Props) {
  const maxCountExceeded = vulnerabilityCount > maxSendCount
  const bugtrackerOptionEnabled =
    !maxCountExceeded && vulnerabilitiesAreFromSameApplication

  const { formatMessage } = useIntl()
  const iconSize: IconSize = size as IconSize

  return (
    <PopoverMenu
      setIsOpen={(isOpen) => {
        setIsRelatedPopoverOpen?.(isOpen)
      }}
      testId="popover-menu"
    >
      <Menu>
        <MenuItem
          testId={sendActionMenuEmailTestId}
          onClick={onSelectEmailOption}
          itemID="email"
          disabled={maxCountExceeded}
        >
          <Tooltip
            enabled={maxCountExceeded}
            body={
              <FormattedMessage
                id={
                  maxCountExceeded
                    ? 'action_vulnerabilitySend_max'
                    : 'detail_vulnerabilities_vulnerabilitiesFromSameApp'
                }
                values={maxCountExceeded ? { max: maxSendCount } : undefined}
              />
            }
          >
            <div>{formatMessage({ id: 'action_sendByEmail' })}</div>
          </Tooltip>
        </MenuItem>

        <MenuItem
          testId={sendActionMenuBugTrackerTestId}
          onClick={
            bugtrackerOptionEnabled ? onSelectBugtrackerOption : undefined
          }
          itemID="bugtracker"
        >
          <Tooltip
            enabled={!bugtrackerOptionEnabled}
            body={
              <FormattedMessage
                id={'detail_libraries_bulkBugTrackerNotAvailable'}
              />
            }
          >
            <div>{formatMessage({ id: 'action_sendToBugTracker' })}</div>
          </Tooltip>
        </MenuItem>
      </Menu>
      <MenuTrigger>
        <ActionButton
          data-testid={actionTestID}
          testId={actionTestID}
          IconButtonProps={{
            Icon: IconSend,
            'aria-label': formatMessage({ id: 'action_send' }),
            size: iconSize
          }}
          TooltipProps={{
            placement: 'top',
            body: formatMessage({ id: 'action_send' })
          }}
        />
      </MenuTrigger>
    </PopoverMenu>
  )
}
